import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ClockAfternoon, Plus, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = ['orders', 'revenue', 'returns', 'inventory', 'performance'];
const FREQS = ['daily', 'weekly', 'monthly'];

export default function ScheduledReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ report_type: 'orders', frequency: 'weekly', email_to: '', filters: {} });

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/scheduled-reports`, { withCredentials: true });
      setReports(data.reports || []);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.email_to) { toast.error('Email is required'); return; }
    try {
      await axios.post(`${API}/api/seller/scheduled-reports`, form, { withCredentials: true });
      toast.success('Scheduled report created');
      setDialogOpen(false);
      fetchReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (rid) => {
    if (!window.confirm('Delete this scheduled report?')) return;
    try {
      await axios.delete(`${API}/api/seller/scheduled-reports/${rid}`, { withCredentials: true });
      toast.success('Report schedule deleted');
      fetchReports();
    } catch { toast.error('Failed to delete'); }
  };

  const freqColor = { daily: 'bg-orange-100 text-orange-700', weekly: 'bg-blue-100 text-blue-700', monthly: 'bg-green-100 text-green-700' };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl" data-testid="scheduled-reports-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Scheduled Reports</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Automate report generation and email delivery</p>
          </div>
          <Button data-testid="add-report-btn" onClick={() => { setForm({ report_type: 'orders', frequency: 'weekly', email_to: '', filters: {} }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Schedule Report
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : reports.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <ClockAfternoon size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No scheduled reports yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => (
              <div key={r.report_id} className="flex items-center justify-between bg-white rounded-xl border border-[#e5e5ea] px-5 py-4" data-testid={`report-${r.report_id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#7d64ff]/10 flex items-center justify-center"><ClockAfternoon size={20} className="text-[#7d64ff]" /></div>
                  <div>
                    <p className="font-semibold text-[#0A0A0A] capitalize">{r.report_type} Report</p>
                    <p className="text-xs text-[#6b6b8a]">To: {r.email_to}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${freqColor[r.frequency] || 'bg-gray-100'}`}>{r.frequency}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{r.is_active ? 'Active' : 'Paused'}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(r.report_id)} className="text-red-500"><Trash size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="report-dialog">
            <DialogHeader><DialogTitle>Schedule Report</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Report Type</Label>
                <select data-testid="report-type" value={form.report_type} onChange={e => setForm(f => ({ ...f, report_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                  {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div><Label>Frequency</Label>
                <select data-testid="report-freq" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                  {FREQS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                </select>
              </div>
              <div><Label>Email To</Label><Input data-testid="report-email" value={form.email_to} onChange={e => setForm(f => ({ ...f, email_to: e.target.value }))} placeholder="reports@company.com" /></div>
              <Button data-testid="save-report-btn" onClick={handleCreate} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">Create Schedule</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
